import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { authAPI, refreshSessionTokens } from '../services/api';
import { clearEntitlementsCache } from '../hooks/useEntitlements';
import { clearPermissionsCache } from '../hooks/usePermissions';
// i18n Stage 3 (2026-09-23): apply the user's saved preferred_language to the
// i18n runtime after /auth/me, but only when the device has no explicit choice.
import { setLanguage, getInitialLocale } from '../i18n';
// Jaime 2026-09-30 (unit-selection persistence): per-condo last-selected unit
// memory — restore the LAST-SELECTED unit on navigation/reload, never the
// default unit as primary behavior.
import { getLastUnitForCondo, setLastUnitForCondo } from '../utils/lastUnit';
// TH1 diagnostic lane (2026-09-30, Kimi spec): selection-trace ring buffer.
import { traceSel } from '../utils/selTrace';
// Beacon lane (2026-10-01): silent state-anomaly diagnostic (TH1 class).
import { beaconStateAnomaly } from '../lib/homeBeacons';

export type UserRole = 'super_admin' | 'admin' | 'sub_admin' | 'directive' | 'treasurer' | 'propietario' | 'resident' | 'familiar' | 'empleado' | 'security' | 'provider' | 'maintenance_staff' | 'visitante';

export interface AccountContext { id: string; kind: 'personal' | 'condo'; condoId?: string; unitId?: string; label: string; role?: string; subType?: string; status?: string; }

export interface CondoMembership { condoId: string; condoName: string; role?: string; unitId?: string; unitNumber?: string; unitLabel?: string; }

export interface OwnedUnit { id: string; unit_number: string; household_name?: string | null; invite_code?: string; condo_id?: string | null; condo_name?: string | null; /** True when the unit comes from an employee assignment (/api/me), not ownership/residence (Jaime 2026-08-31). */ assigned?: boolean; }

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  condoId: string;
  assignedCondos?: string[];
  unit?: string;
  unitLabel?: string;
  /** Profile picture URL from the backend (/api/uploads/avatars/...); null -> UI falls back to initials. */
  avatarUrl?: string | null;
  plan?: 'free' | 'starter' | 'professional' | 'enterprise';
  contexts?: AccountContext[];
  activeContextId?: string;
  roles?: CondoMembership[];
  memberships?: CondoMembership[];
  activeCondoId?: string;
  /** i18n Stage 3: server-saved language preference ('en' | 'es-MX' | ...); optional. */
  preferred_language?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithPassword: (email: string, password: string) => Promise<boolean>;
  completeLoginWithPayload: (payload: any) => Promise<boolean>;
  verify2FA: (pendingToken: string, code: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  hasRole: (roles: UserRole[]) => boolean;
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  isDirective: () => boolean;
  isResident: () => boolean;
  isSecurity: () => boolean;
  contexts: AccountContext[];
  activeContextId?: string;
  activateContext: (contextId: string) => Promise<void>;
  /** FIX #92 (2026-09-23): re-issue session tokens bound to the personal (Hogar) context. */
  activatePersonalContext: () => Promise<void>;
  roles: CondoMembership[];
  memberships: CondoMembership[];
  activeCondoId?: string;
  setActiveCondo: (condoId: string | null, preserveOwnedUnit?: boolean) => Promise<void>;
  ownedUnits: OwnedUnit[];
  activeOwnedUnitId?: string;
  setActiveOwnedUnit: (unitId?: string) => void;
  /** 2026-09-24 (Jaime 12:05 refinement, RESIDENCY semantics): true when the
   * user should see the pure Personal (Hogar) entry. Server truth: GET
   * /auth/contexts + /auth/me `should_show_personal` (= !is_resident_anywhere,
   * resident/familiar unit-bound). Client-side derivation fallback matches the
   * BE definition: any membership row with role residente/resident/familiar
   * bound to a unit. Default true while hydrating so nothing flashes.
   * TODO(server): remove the derivation once the server flag ships on every
   * payload (login, /auth/me, contexts). */
  showPersonalFlag: boolean;
  /** INFORMATIONAL ONLY (ownership semantics — do NOT gate the Personal entry
   * with this). Server `has_units`: true when the user owns or has any active
   * unit membership. Sole consumer: CreateHomeNudge (targets the truly
   * home-less, including an owner-only non-resident). */
  hasUnits: boolean;
  refreshMe: () => Promise<void>;
  /** True once the first owned-units hydration has settled (see unitsLoaded). */
  unitsLoaded: boolean;
}

// FIX #110 (2026-09-24): dev-only HMR persistence. When Vite Fast-Refresh
// re-evaluates this module, `createContext` would mint a NEW context object
// while stale components still call useContext() with the old one -> transient
// dev crash "useAuth must be used within AuthProvider" (bug #110, 1x
// 2026-09-24 03:00:44Z on dev 5173). Reusing the module-scope instance across
// HMR re-evals keeps provider and consumers on the same context. No effect in
// prod builds (import.meta.env.DEV is false -> plain createContext path).
const AuthContext: ReturnType<typeof createContext<AuthContextType | undefined>> =
  (import.meta.env.DEV && (globalThis as any).__vecindusAuthContext) ||
  createContext<AuthContextType | undefined>(undefined);
if (import.meta.env.DEV) (globalThis as any).__vecindusAuthContext = AuthContext;

/**
 * FIX #110 (2026-09-24): non-throwing variant of useAuth. Returns undefined
 * when the calling component is transiently rendered outside AuthProvider
 * (dev-only HMR edge case). Callers that can tolerate an unauthenticated
 * fallback should use this instead of useAuth().
 */
export function useAuthOptional(): AuthContextType | undefined {
  return useContext(AuthContext);
}

/**
 * Persist auth state to all localStorage keys that the rest of the app reads.
 *
 * - `vecindus_auth` — { user, token, timestamp } used for session restoration
 * - `token`         — raw JWT string, read by the axios interceptor
 * - `user`          — JSON string, read by the axios interceptor for X-User-* headers
 */
function persistAuth(user: User, token: string) {
  const payload = JSON.stringify({ user, token, timestamp: Date.now() });
  localStorage.setItem('vecindus_auth', payload);
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
}

function clearAuth() {
  localStorage.removeItem('vecindus_auth');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  // Session fix (2026-09-17): intentional logout clears BOTH tokens so the
  // silent-refresh flow can never resurrect a session the user ended.
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('vecindus_active_condo');
  localStorage.removeItem('vecindus_active_context');
  // P0-1 fix: logout must not leak user context into the next session.
  localStorage.removeItem('vecindus_active_owned_unit');
  // TH1 stale-id lane (2026-10-01): the known-unit snapshot must never leak
  // into another account's session.
  localStorage.removeItem('vecindus_known_unit_ids');
  localStorage.removeItem('vecindus_lens');
  localStorage.removeItem('vecindus_condo_name');
  localStorage.removeItem('vecindus_2fa_pending');
  // Clear entitlement/permission caches via their exported helpers.
  clearEntitlementsCache();
  clearPermissionsCache();
  // Defensive sweep for any vecindus_entitlements:* / vecindus_permissions:* entries.
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('vecindus_entitlements:') || key.startsWith('vecindus_permissions:'))
      .forEach((key) => localStorage.removeItem(key));
  } catch { /* storage optional */ }
}

/**
 * i18n Stage 3 (2026-09-23, revised): after a successful /auth/me (login,
 * restore, 2FA), apply the user's server-saved preferred language so their
 * chosen UI language follows them across devices/sessions. GET /auth/me
 * exposes the normalized value as `preferredLanguage` (camelCase, legacy 'es'
 * already normalized to 'es-MX' by the backend); accept the 4 supported
 * locales and defensively normalize a legacy 'es'. When it differs from the
 * current device locale, switch and refresh the 'v-lang' cache. Fire-and-
 * forget: language sync must never break auth; profile fetch failure keeps
 * the current language.
 */
export function applyServerPreferredLanguage(user: unknown): void {
  const source = user as { preferredLanguage?: string; preferred_language?: string } | null | undefined;
  const raw = source?.preferredLanguage || source?.preferred_language || '';
  if (!raw) return;
  const locale = ['es-MX', 'es-VE', 'es-ES', 'en'].includes(raw)
    ? raw
    : (raw === 'es' ? 'es-MX' : null);
  if (!locale) return;
  // Current device locale: explicit 'v-lang' choice, else the bootstrap locale.
  let current = '';
  try { current = localStorage.getItem('v-lang') || ''; } catch { /* storage optional */ }
  if (!current) current = getInitialLocale();
  if (current === locale) return;
  void setLanguage(locale)
    .then((resolved) => {
      try { localStorage.setItem('v-lang', resolved); } catch { /* storage optional */ }
    })
    .catch(() => { /* keep current locale */ });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Start loading to check storage
  const [contexts, setContexts] = useState<AccountContext[]>([]);
  // 2026-09-24: server truth for "user has a home unit" (GET /auth/contexts
  // has_units — INFORMATIONAL, ownership semantics; see hasUnits below).
  const [hasUnitsFlag, setHasUnitsFlag] = useState<boolean | null>(null);
  // 2026-09-24 (Jaime 12:05 refinement, RESIDENCY gating): server truth for
  // is_resident_anywhere (resident/familiar unit-bound) from /auth/contexts +
  // /auth/me should_show_personal/is_resident_anywhere. null = not seen yet ->
  // derive client-side from membership rows.
  const [residentAnywhereFlag, setResidentAnywhereFlag] = useState<boolean | null>(null);
  const [activeContextId, setActiveContextId] = useState<string | undefined>();
  const [roles, setRoles] = useState<CondoMembership[]>([]);
  const [activeCondoId, setActiveCondoId] = useState<string | undefined>();
  const [ownedUnits, setOwnedUnits] = useState<OwnedUnit[]>([]);
  // F-2 (2026-09-23): true once the first owned-units hydration (/units/mine via
  // refreshMe) has settled. Consumers that gate "membership-less" behavior on
  // ownedUnits being empty (AppShell minimalMode) must wait for this flag —
  // otherwise a standalone unit owner is bounced to /resident-qr in the race
  // before the units fetch resolves.
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [activeOwnedUnitId, setActiveOwnedUnitId] = useState<string | undefined>(() => localStorage.getItem('vecindus_active_owned_unit') || undefined);

  // 2026-09-24 (residency gating): capture should_show_personal /
  // is_resident_anywhere defensively from any auth payload (login,
  // /auth/contexts, /auth/me). Also tolerates has_units for the informational
  // flag. Ignored when absent.
  const captureResidencyFlags = (payload: unknown) => {
    const p = payload as Record<string, unknown> | null | undefined;
    if (!p || typeof p !== 'object') return;
    const ssp = p.should_show_personal;
    const ira = p.is_resident_anywhere;
    if (typeof ssp === 'boolean') setResidentAnywhereFlag(!ssp);
    else if (typeof ira === 'boolean') setResidentAnywhereFlag(ira);
    if (typeof p.has_units === 'boolean') setHasUnitsFlag(p.has_units);
  };

  // Load auth state from localStorage on mount
  //
  // Session fix (2026-09-17, Jaime-approved): the artificial 7-day client cap
  // is GONE. Access-token expiry is handled by the backend (401) + the axios
  // silent-refresh interceptor. If no access token can be restored but a
  // refresh token exists in localStorage, we silently call
  // POST /api/auth/refresh to re-establish the session BEFORE showing login.
  useEffect(() => {
    let cancelled = false;
    const finish = () => { if (!cancelled) setLoading(false); };

    // Launch with a refresh token only (e.g. iOS Safari purged the access
    // token blob): refresh silently, hydrate the user via /auth/me, restore.
    const trySilentRestore = async (): Promise<boolean> => {
      const refreshed = await refreshSessionTokens();
      if (!refreshed) return false;
      try {
        const me = await authAPI.getMe();
        const mePayload = me.data?.data || me.data?.user || me.data;
        const nextUser = mePayload?.user || mePayload;
        if (!nextUser?.id) return false;
        const restoreMemberships: CondoMembership[] = mePayload?.memberships || mePayload?.roles || nextUser.memberships || nextUser.roles || [];
        const restoreContexts: AccountContext[] = Array.isArray(mePayload?.contexts) ? mePayload.contexts : (Array.isArray(nextUser.contexts) ? nextUser.contexts : []);
        const restoreActiveContextId = localStorage.getItem('vecindus_active_context') || mePayload?.activeContextId || nextUser.activeContextId;
        const unitLabel = nextUser.unitLabel || nextUser.unit || restoreMemberships[0]?.unitLabel || restoreMemberships[0]?.unitNumber || null;
        const userData: User = {
          ...nextUser,
          condoId: nextUser.condoId || restoreMemberships[0]?.condoId || '',
          unitLabel,
          unit: unitLabel,
          roles: restoreMemberships,
          memberships: restoreMemberships,
          contexts: restoreContexts,
          activeContextId: restoreActiveContextId,
        };
        persistAuth(userData, refreshed.accessToken);
        applyServerPreferredLanguage(nextUser);
        if (cancelled) return true;
        setUser(userData);
        setContexts(restoreContexts);
        setActiveContextId(restoreActiveContextId);
        setRoles(restoreMemberships);
        setActiveCondoId(localStorage.getItem('vecindus_active_condo') || restoreMemberships[0]?.condoId || nextUser.condoId);
        setIsAuthenticated(true);
        return true;
      } catch {
        return false;
      }
    };

    (async () => {
      const storedAuth = localStorage.getItem('vecindus_auth');
      let restored = false;
      if (storedAuth) {
        try {
          const { user: storedUser, token: storedToken } = JSON.parse(storedAuth);
          if (storedUser && storedToken) {
          // FIX (2026-09-01, Jaime): activateContext used to overwrite user.id with
          // the account-context id, so task-creator checks (isCreator) never matched.
          // Self-heal sessions persisted with the clobbered id by re-reading the real
          // users.id from the JWT payload (claims.userId, set by generateToken).
          let healedUser = storedUser;
          try {
            const jwtUserId = JSON.parse(atob(storedToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))?.userId;
            if (jwtUserId && storedUser.id !== jwtUserId) healedUser = { ...storedUser, id: jwtUserId };
          } catch { /* malformed token: leave as-is */ }
          setUser(healedUser);
          setContexts(storedUser.contexts || []);
          setActiveContextId(localStorage.getItem('vecindus_active_context') || storedUser.activeContextId);
          const storedRoles: CondoMembership[] = storedUser.memberships || storedUser.roles || [];
          setRoles(storedRoles);
          const storedCondo = localStorage.getItem('vecindus_active_condo');
          const storedCondoIds = new Set([
            ...storedRoles.map((entry: CondoMembership) => entry.condoId),
            ...(storedUser.contexts || []).filter((entry: AccountContext) => entry.kind === 'condo' && entry.condoId).map((entry: AccountContext) => entry.condoId as string),
          ]);
          const validStoredCondo = storedCondo && storedCondoIds.has(storedCondo) ? storedCondo : undefined;
          if (storedCondo && !validStoredCondo) localStorage.removeItem('vecindus_active_condo');
          setActiveCondoId(validStoredCondo || storedRoles[0]?.condoId || storedUser.condoId);
          setIsAuthenticated(true);
          // Ensure token and user keys are also populated for the axios interceptor
          if (storedToken) {
            localStorage.setItem('token', storedToken);
            localStorage.setItem('user', JSON.stringify(healedUser));
          }
          restored = true;
          }
        } catch {
          restored = false;
        }
      }
      if (!restored) {
        // No restorable access token: try the silent refresh flow before
        // falling back to the login screen. On total failure, wipe stale keys.
        const ok = await trySilentRestore();
        if (!ok && !cancelled) clearAuth();
      }
      finish();
    })();
    return () => { cancelled = true; };
  }, []);

  // Save auth state to localStorage when it changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // Re-read token from localStorage (persistAuth already set it during login)
      const token = localStorage.getItem('token') || '';
      persistAuth(user, token);
    }
  }, [isAuthenticated, user]);

  const hasRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const isSuperAdmin = (): boolean => user?.role === 'super_admin';
  const isAdmin = (): boolean => user?.role === 'admin' || user?.role === 'super_admin';
  const isDirective = (): boolean => user?.role === 'directive' || user?.role === 'super_admin';
  const isResident = (): boolean => user?.role === 'resident';
  const isSecurity = (): boolean => user?.role === 'security';

  const performLogin = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    // Identity batch 1 (2026-09-15): the web UI now calls the phone-capable
    // POST /api/auth/login-password (identifier in the `email` field — email
    // first, normalized-phone fallback). Endpoints are NOT merged; /login
    // remains for legacy callers. requires2fa/pendingToken handling below is
    // identical for both endpoints.
    const response = await fetch('/api/auth/login-password', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setLoading(false);
      // D1 fix (2026-10-01): a 429 from the shared-IP login rate limiter is
      // NOT bad credentials. Carry the HTTP status + retry hint on the error
      // so the login form can surface the backend's wait-time message instead
      // of the generic "Credenciales inválidas".
      const err = new Error(data.error || data.message || 'Invalid credentials') as Error & { status?: number; retryAfterSeconds?: number; isRateLimit?: boolean; code?: string; email?: string };
      err.status = response.status;
      err.retryAfterSeconds = data.retry_after_seconds;
      err.isRateLimit = response.status === 429;
      // P1 launch-blocker fix (2026-10-09): carry the backend error code so the
      // login form can show the verify-email resend affordance.
      err.code = data.code;
      err.email = data.data?.email;
      throw err;
    }
    const loginData = data.data || data;
    if (loginData.requires2fa) { localStorage.setItem('vecindus_2fa_pending', loginData.pendingToken || loginData.data?.pendingToken || ''); setLoading(false); return false; }
    const memberships: CondoMembership[] = loginData.memberships || loginData.roles || data.memberships || data.roles || loginData.user?.memberships || loginData.user?.roles || [];
    const firstMembership = memberships[0];
    const loginContexts: AccountContext[] = Array.isArray(loginData.contexts) ? loginData.contexts : (Array.isArray(loginData.user?.contexts) ? loginData.user.contexts : []);
    // 2026-09-24: capture the server has_units flag when the login payload
    // carries it (BE child contract); ignored when absent.
    captureResidencyFlags(loginData);
    captureResidencyFlags(loginData.user);
    const loginActiveContextId = loginData.activeContextId || loginData.user?.activeContextId;
    const userData: User = {
      ...loginData.user,
      condoId: loginData.user?.condoId || firstMembership?.condoId || '',
      unitLabel: loginData.user?.unitLabel || loginData.user?.unit || firstMembership?.unitLabel || firstMembership?.unitNumber || null,
      unit: loginData.user?.unitLabel || loginData.user?.unit || firstMembership?.unitLabel || firstMembership?.unitNumber || null,
      roles: memberships,
      memberships,
      contexts: loginContexts,
      activeContextId: loginActiveContextId,
    };
    const token: string = loginData.token || loginData.tokens?.accessToken || loginData.accessToken || '';
    // iOS session fix (2026-09-18): persist the refresh token on password login
    // too — previously only 2FA/activateContext paths stored it, so the silent
    // refresh in api.ts had nothing to use after an access-token expiry.
    const rt: string | undefined = loginData.tokens?.refreshToken || loginData.refreshToken;
    if (rt) localStorage.setItem('refreshToken', rt);

    // Persist to all keys the app reads (token, user, vecindus_auth)
    persistAuth(userData, token);
    applyServerPreferredLanguage(userData);

    setUser(userData);
    setContexts(loginContexts);
    setActiveContextId(loginActiveContextId);
    setRoles(memberships);
    const persistedCondo = localStorage.getItem('vecindus_active_condo');
    const loginCondoIds = new Set([
      ...memberships.map((entry) => entry.condoId),
      ...loginContexts.filter((entry) => entry.kind === 'condo' && entry.condoId).map((entry) => entry.condoId as string),
    ]);
    const selectedCondo = persistedCondo && loginCondoIds.has(persistedCondo)
      ? persistedCondo
      : firstMembership?.condoId || loginContexts.find((entry) => entry.kind === 'condo' && entry.condoId)?.condoId;
    if (persistedCondo && !loginCondoIds.has(persistedCondo)) localStorage.removeItem('vecindus_active_condo');
    setActiveCondoId(selectedCondo);
    if (selectedCondo) localStorage.setItem('vecindus_active_condo', selectedCondo);
    setIsAuthenticated(true);
    setLoading(false);
    return true;
  };

  const login = async (email: string, password: string) => performLogin(email, password);
  const loginWithPassword = async (email: string, password: string) => performLogin(email, password);

  // Identity batch 2 (item B, 2026-09-16): email-OTP login. The LoginPage
  // performs POST /api/auth/request-email-otp + /api/auth/verify-email-otp
  // itself and hands the SUCCESS payload here; this applies the exact same
  // persistence/membership mapping as performLogin (token, user, contexts).
  // Returns false when the payload requires 2FA (pendingToken stored).
  const completeLoginWithPayload = async (payload: any): Promise<boolean> => {
    setLoading(true);
    try {
      const data = payload?.data || payload || {};
      if (data.requires2fa) {
        localStorage.setItem('vecindus_2fa_pending', data.pendingToken || data.data?.pendingToken || '');
        return false;
      }
      const memberships: CondoMembership[] = data.memberships || data.roles || data.user?.memberships || data.user?.roles || [];
      const firstMembership = memberships[0];
      const loginContexts: AccountContext[] = Array.isArray(data.contexts) ? data.contexts : (Array.isArray(data.user?.contexts) ? data.user.contexts : []);
      const loginActiveContextId = data.activeContextId || data.user?.activeContextId;
      const userData: User = {
        ...data.user,
        condoId: data.user?.condoId || firstMembership?.condoId || '',
        unitLabel: data.user?.unitLabel || data.user?.unit || firstMembership?.unitLabel || firstMembership?.unitNumber || null,
        unit: data.user?.unitLabel || data.user?.unit || firstMembership?.unitLabel || firstMembership?.unitNumber || null,
        roles: memberships,
        memberships,
        contexts: loginContexts,
        activeContextId: loginActiveContextId,
      };
      const token: string = data.token || data.tokens?.accessToken || data.accessToken || '';
      // iOS session fix (2026-09-18): persist the refresh token here as well.
      const rt: string | undefined = data.tokens?.refreshToken || data.refreshToken;
      if (rt) localStorage.setItem('refreshToken', rt);
      persistAuth(userData, token);
      applyServerPreferredLanguage(userData);
      setUser(userData);
      setContexts(loginContexts);
      setActiveContextId(loginActiveContextId);
      setRoles(memberships);
      const persistedCondo = localStorage.getItem('vecindus_active_condo');
      const loginCondoIds = new Set([
        ...memberships.map((entry) => entry.condoId),
        ...loginContexts.filter((entry) => entry.kind === 'condo' && entry.condoId).map((entry) => entry.condoId as string),
      ]);
      const selectedCondo = persistedCondo && loginCondoIds.has(persistedCondo)
        ? persistedCondo
        : firstMembership?.condoId || loginContexts.find((entry) => entry.kind === 'condo' && entry.condoId)?.condoId;
      if (persistedCondo && !loginCondoIds.has(persistedCondo)) localStorage.removeItem('vecindus_active_condo');
      setActiveCondoId(selectedCondo);
      if (selectedCondo) localStorage.setItem('vecindus_active_condo', selectedCondo);
      setIsAuthenticated(true);
      return true;
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async (pendingToken: string, code: string) => {
    const response = await authAPI.verify2FA(pendingToken, code);
    const payload = response.data?.data || response.data;
    const token = payload.accessToken || payload.token || payload.tokens?.accessToken || '';
    const verifyContexts: AccountContext[] = Array.isArray(payload.contexts) ? payload.contexts : (Array.isArray(payload.user?.contexts) ? payload.user.contexts : []);
    const verifyActiveContextId = payload.activeContextId || payload.user?.activeContextId;
    // Persist refresh token like the login path does.
    const refreshToken = payload.tokens?.refreshToken || payload.refreshToken;
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    // If /2fa/verify omits the user payload, hydrate it via /auth/me instead of failing.
    let userSource = payload.user;
    if (!userSource && token) {
      try {
        const me = await authAPI.getMe();
        userSource = me.data?.data || me.data?.user || me.data;
      } catch { userSource = undefined; }
    }
    const userData: User = { ...userSource, contexts: verifyContexts, activeContextId: verifyActiveContextId, unitLabel: userSource?.unitLabel || userSource?.unit || null, unit: userSource?.unitLabel || userSource?.unit || null };
    if (!token || !userSource) throw new Error('Respuesta de autenticación inválida');
    persistAuth(userData, token); applyServerPreferredLanguage(userData); setUser(userData); setContexts(verifyContexts); setActiveContextId(verifyActiveContextId); setIsAuthenticated(true);
  };

  const activateContext = async (contextId: string) => {
    const response = await authAPI.activateContext(contextId);
    const data = response.data?.data || response.data;
    const accessToken = data?.accessToken;
    if (!accessToken || !data?.activeContext) throw new Error('La respuesta no contiene tokens de sesión');
    const refreshToken = data.refreshToken;
    localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    const activeContext = data.activeContext as AccountContext;
    // Context kind determines the shell lens. Persist it before reload so a condo
    // selection cannot leave the user on the personal Compras/Tareas navigation.
    localStorage.setItem('vecindus_lens', activeContext.kind === 'condo' ? 'building' : 'home');
    localStorage.setItem('vecindus_active_context', activeContext.id);
    const nextContexts: AccountContext[] = Array.isArray(data.contexts) ? data.contexts : contexts;
    const nextCondoId = activeContext.kind === 'condo' ? (activeContext.condoId || user?.condoId) : undefined;
    // Persist the context selection so it survives the reload below.
    let nextOwnedUnit: OwnedUnit | undefined;
    if (activeContext.kind === 'condo' && nextCondoId) {
      localStorage.setItem('vecindus_active_condo', nextCondoId);
      const storedUnitId = localStorage.getItem('vecindus_active_owned_unit');
      // H1 FIX (2026-09-30, Kimi analysis): after a personal-lens dip both
      // vecindus_active_condo and vecindus_active_owned_unit are cleared, so
      // the storedUnitId branch above misses on the way back to the condo and
      // the chain fell through to the DEFAULT unit. Consult the per-condo
      // last-selected memory BEFORE the default-unit fallback — last-selected
      // remains the primary behavior, default is last resort.
      const memoryUnitId = getLastUnitForCondo(nextCondoId);
      nextOwnedUnit =
        ownedUnits.find((u) => u.id === activeContext.unitId && u.condo_id === nextCondoId) ||
        (storedUnitId ? ownedUnits.find((u) => u.id === storedUnitId && u.condo_id === nextCondoId) : undefined) ||
        (memoryUnitId ? ownedUnits.find((u) => u.id === memoryUnitId && u.condo_id === nextCondoId) : undefined) ||
        ownedUnits.find((u) => u.condo_id === nextCondoId);
      if (nextOwnedUnit) {
        localStorage.setItem('vecindus_active_owned_unit', nextOwnedUnit.id);
        setLastUnitForCondo(nextCondoId, nextOwnedUnit.id);
      } else {
        localStorage.removeItem('vecindus_active_owned_unit');
      }
    } else {
      // Personal ("Hogar") context: drop the condo selection and any condo-owned unit.
      localStorage.removeItem('vecindus_active_condo');
      if (activeContext.unitId) {
        nextOwnedUnit = ownedUnits.find((u) => u.id === activeContext.unitId);
        if (nextOwnedUnit) localStorage.setItem('vecindus_active_owned_unit', nextOwnedUnit.id);
        else localStorage.removeItem('vecindus_active_owned_unit');
      } else {
        // Personal contexts carry no unitId. Fallback order: stored unit WITHOUT condo_id,
        // then the first owned unit without condo_id (the unlinked home). Never keep a
        // condo-linked unit (e.g. Allegra 601) selected after switching to Hogar.
        const storedUnitId = localStorage.getItem('vecindus_active_owned_unit');
        const storedUnit = storedUnitId ? ownedUnits.find((u) => u.id === storedUnitId) : undefined;
        nextOwnedUnit =
          (storedUnit && !storedUnit.condo_id ? storedUnit : undefined) ||
          ownedUnits.find((u) => !u.condo_id);
        if (nextOwnedUnit) localStorage.setItem('vecindus_active_owned_unit', nextOwnedUnit.id);
        else localStorage.removeItem('vecindus_active_owned_unit');
      }
    }
    const nextUser = { ...(user || {}), ...activeContext, id: user?.id ?? activeContext.id, role: user?.role || activeContext.role, condoId: nextCondoId || '', contexts: nextContexts, activeContextId: activeContext.id, unit: nextOwnedUnit?.household_name || nextOwnedUnit?.unit_number, unitLabel: nextOwnedUnit?.household_name || nextOwnedUnit?.unit_number } as User; // FIX 2026-08-30: prefer unit name over number
    persistAuth(nextUser, accessToken);
    setUser(nextUser); setContexts(nextContexts); setActiveContextId(activeContext.id);
    setActiveCondoId(nextCondoId);
    setActiveOwnedUnitId(nextOwnedUnit?.id);
    // TH1 diagnostic lane: trace the full context activation before the reload.
    traceSel('activateContext', { contextId: activeContext.id, kind: activeContext.kind, condoId: nextCondoId ?? null, contextUnitId: activeContext.unitId ?? null, storedUnitId: localStorage.getItem('vecindus_active_owned_unit'), chosenUnit: nextOwnedUnit?.id ?? null });
    window.location.reload();
  };

  /**
   * FIX #92 (2026-09-23, bug board): switching to Hogar only mutated local state —
   * the JWT kept the condo contextId, so after a reload GET /auth/me returned
   * activeContextId = condo context (req.contextId from the token) and the UI
   * "switched back" to the condo. This helper activates the user's PERSONAL
   * context server-side (POST /auth/contexts/:id/activate), which re-issues
   * access/refresh tokens with contextId = personal context, and persists the
   * result. Degrades to the legacy local-only behavior if the endpoint or the
   * personal context is unavailable.
   *
   * LEAK FIX (2026-09-24, Jaime/Jose correctness): a condo-linked active unit
   * is DROPPED on personal activation — vecindus_active_owned_unit and the
   * X-Active-Unit-Id header state are cleared so "Personal" can never silently
   * serve another home's data (the TH1-under-Personal bleed). Standalone
   * (unlinked) home units are the user's OWN home and keep driving
   * X-Active-Unit-Id for the standalone-home flows, so they are preserved.
   */
  const activatePersonalContext = async () => {
    let personal = contexts.find((c) => c.kind === 'personal');
    if (!personal) {
      try {
        const res = await authAPI.getContexts();
        const payload = res.data?.data || res.data;
        // 2026-09-24: prefer the server residency/has_units flags when present.
        captureResidencyFlags(payload);
        const list: AccountContext[] = Array.isArray(payload?.contexts) ? payload.contexts : [];
        setContexts((prev) => (prev.length ? prev : list));
        personal = list.find((c) => c.kind === 'personal');
      } catch { /* degrade below */ }
    }
    if (!personal) return;
    // LEAK FIX (2026-09-24): never let Personal borrow a condo-linked unit.
    // Applies to every path below (reconcile, server activation, legacy local).
    // TH1 personal-lens wipe lane (2026-10-01, single semantic): this path is a
    // CLEAR, not a borrow-and-clear. It drops ONLY the active unit id
    // (vecindus_active_owned_unit + state) so Personal can never serve condo
    // data. It must NEVER touch the per-condo memory keys
    // (vecindus_last_unit_<condoId>) nor vecindus_last_condo — the condo the
    // user was just in stays recoverable via the per-condo restore path. The
    // "borrowed*" names below are legacy; nothing is kept in a borrowed slot.
    // 2026-09-24 (kimi minor 4, defensive): ownedUnits may not be hydrated yet
    // when this runs (e.g. right after login), so the closure lookup can miss a
    // stale unit that IS still persisted. Re-check vecindus_active_owned_unit
    // directly; an id present in storage but absent from a hydrated ownedUnits
    // list is treated as condo-linked (conservative) and dropped, which matches
    // refreshMe's restore logic (unknown unit ids are discarded there anyway).
    const storedBorrowedUnitId = localStorage.getItem('vecindus_active_owned_unit');
    const borrowedUnit = ownedUnits.find((u) => u.id === activeOwnedUnitId)
      || (storedBorrowedUnitId ? ownedUnits.find((u) => u.id === storedBorrowedUnitId) : undefined)
      || (storedBorrowedUnitId && ownedUnits.length > 0 ? { id: storedBorrowedUnitId, condo_id: '__unknown__' } : undefined);
    traceSel('activatePersonalContext', { personalId: personal.id, activeUnitId: activeOwnedUnitId ?? null, borrowedUnitId: borrowedUnit?.id ?? null, borrowedCondoId: borrowedUnit?.condo_id ?? null, cleared: !!borrowedUnit?.condo_id });
    if (borrowedUnit?.condo_id) {
      setActiveOwnedUnit(undefined); // clears state + vecindus_active_owned_unit (X-Active-Unit-Id)
      setUser((current) => current ? { ...current, unit: undefined, unitLabel: undefined } : current);
    }
    localStorage.setItem('vecindus_active_context', personal.id);
    localStorage.setItem('vecindus_lens', 'home');
    if (activeContextId === personal.id) {
      // Token already bound to the personal context; just reconcile local state.
      setActiveContextId(personal.id);
      setUser((current) => current ? { ...current, condoId: '', activeContextId: personal.id } : current);
      return;
    }
    try {
      const response = await authAPI.activateContext(personal.id);
      const data = response.data?.data || response.data;
      if (data?.accessToken) {
        localStorage.setItem('token', data.accessToken);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      }
      setActiveContextId(personal.id);
      setUser((current) => current ? { ...current, condoId: '', activeContextId: personal.id } : current);
    } catch {
      // Older backend without /auth/contexts — keep the legacy local-only behavior.
    }
  };

  const setActiveCondo = async (condoId: string | null, preserveOwnedUnit = false) => {
    if (condoId === null) {
      try {
        await authAPI.setActiveCondo(null);
      } catch {
        // Older backends may not accept null; local context still supports personal homes.
      }
      localStorage.removeItem('vecindus_active_condo');
      localStorage.removeItem('vecindus_active_context');
      localStorage.setItem('vecindus_lens', 'home');
      setActiveCondoId(undefined);
      setActiveContextId(undefined);
      setUser((current) => current ? { ...current, condoId: '', activeContextId: undefined } : current);
      // FIX #92 (2026-09-23): bind the session to the personal context so the
      // switch survives a reload (JWT contextId was left pointing at the condo).
      await activatePersonalContext();
      return;
    }
    const membership = roles.find((entry) => entry.condoId === condoId);
    const ownedUnit = ownedUnits.find((unit) => unit.condo_id === condoId);
    // A linked owned unit is a valid context even when /me does not expose a membership row.
    if (!membership && !ownedUnit) return;
    await authAPI.setActiveCondo(condoId);
    localStorage.setItem('vecindus_active_condo', condoId);
    // TH1 personal-lens wipe lane (2026-10-01): remember the last condo under a
    // key the personal-lens paths NEVER clear, so returning from the personal
    // lens (condo tab or switcher) can restore this condo through the verified
    // per-condo last-unit restore path even after a reload while the personal
    // context is active (refreshMe drops vecindus_active_condo under a personal
    // JWT by design; vecindus_last_condo survives it).
    localStorage.setItem('vecindus_last_condo', condoId);
    // FIX 2026-09-03 (Jaime): a unit switch (preserveOwnedUnit=true) must land and
    // STAY on the home lens for every non-security role. The previous unconditional
    // 'building' left vecindus_lens='building' (and vecindus_active_context on the
    // condo context) after a mobile unit switch of a condo-linked unit, so an
    // interrupted navigation or reload reconciled the resident back to the building
    // lens/dashboard. Explicit condo selection (preserveOwnedUnit=false) keeps 'building'.
    localStorage.setItem('vecindus_lens', preserveOwnedUnit ? 'home' : 'building');
    setActiveCondoId(condoId);
    const condoContext = contexts.find((context) => context.kind === 'condo' && context.condoId === condoId);
    if (condoContext) localStorage.setItem('vecindus_active_context', condoContext.id);
    setActiveContextId(condoContext?.id);
    if (!preserveOwnedUnit) {
      // Jaime 2026-09-30: switching condos restores the LAST-SELECTED unit of
      // the target condo (per-condo memory) instead of wiping the selection and
      // letting the UI fall through to the default unit. Only when no valid
      // last-selected unit exists for that condo does the selection stay empty
      // (unit-scoped pages then show the unit-picker prompt state).
      const lastUnit = resolveLastUnitForCondo(condoId);
      traceSel('setActiveCondo', { condoId, preserveOwnedUnit, restoredLastUnit: lastUnit?.id ?? null });
      setActiveOwnedUnit(lastUnit?.id);
    } else {
      traceSel('setActiveCondo', { condoId, preserveOwnedUnit });
    }
    setUser((current) => current ? { ...current, condoId, activeContextId: condoContext?.id, unit: membership?.unitLabel || membership?.unitNumber || ownedUnit?.unit_number, unitLabel: membership?.unitLabel || membership?.unitNumber || ownedUnit?.unit_number } : current);
  };

  const setActiveOwnedUnit = (unitId?: string) => {
    setActiveOwnedUnitId(unitId);
    if (unitId) localStorage.setItem('vecindus_active_owned_unit', unitId);
    else localStorage.removeItem('vecindus_active_owned_unit');
    // Jaime 2026-09-30 (per-condo last-selected memory): every successful unit
    // pick is remembered under the condo it belongs to, so returning to that
    // condo restores the LAST-SELECTED unit instead of the default. Clearing
    // the selection never wipes the memory — that is the point of the fix.
    if (unitId) {
      const unit = ownedUnits.find((u) => u.id === unitId);
      const condoOfUnit = unit
        ? (unit.condo_id || null)
        : ((roles || []).find((r) => ((r.unitId || (r as unknown as Record<string, unknown>).unit_id) as string | undefined) === unitId)?.condoId ?? null);
      setLastUnitForCondo(condoOfUnit, unitId);
      traceSel('setActiveOwnedUnit', { unitId, condoOfUnit: condoOfUnit ?? null });
    } else {
      traceSel('setActiveOwnedUnit', { unitId: null });
    }
  };

  // Resolve the LAST-SELECTED unit remembered for a condo, validating it
  // against the current units list / membership rows (Jaime 2026-09-30).
  // H1/trace revision (2026-09-30, Kimi): a failed match NEVER destroys the
  // memory — a miss is usually a transient hydration gap (ownedUnits not yet
  // loaded) or a personal-lens dip, not a dead unit. The entry is only
  // overwritten after a VERIFIED new selection (setActiveOwnedUnit) or a
  // verified refreshMe repair, so the remember/restore contract survives
  // cross-lens dips.
  const resolveLastUnitForCondo = (condoId: string | null | undefined): OwnedUnit | undefined => {
    const stored = getLastUnitForCondo(condoId);
    if (!stored) return undefined;
    const owned = ownedUnits.find((u) => u.id === stored && (condoId ? u.condo_id === condoId : !u.condo_id));
    if (owned) return owned;
    const membershipBacks = (roles || []).some((r) => {
      const mid = (r.unitId || (r as unknown as Record<string, unknown>).unit_id) as string | undefined;
      if (mid !== stored) return false;
      const mcondo = (r.condoId || (r as unknown as Record<string, unknown>).condo_id) as string | null | undefined;
      return condoId ? mcondo === condoId : !mcondo;
    });
    if (membershipBacks) return { id: stored, unit_number: '' } as OwnedUnit;
    // Preserve memory on a failed match; only record the miss for diagnosis.
    traceSel('lastUnitMiss', { condoId: condoId ?? null, storedUnitId: stored });
    return undefined;
  };

  // 2026-09-24 (Jaime ruling, refined 12:05): has_units is INFORMATIONAL
  // (ownership semantics) — only CreateHomeNudge consumes it.
  const derivedHasUnits = ownedUnits.length > 0 || roles.some((m) => !!m.unitId);
  const hasUnits = hasUnitsFlag ?? (unitsLoaded ? derivedHasUnits : true);

  // 2026-09-24 (Jaime 12:05 refinement, RESIDENCY gating): show Personal unless
  // the user is a resident/familiar/inquilino bound to a unit. Server flag
  // (should_show_personal / is_resident_anywhere) first; fallback derivation
  // matches the BE definition: any membership row with role
  // residente/resident/familiar/inquilino bound to a unit. Owner-only
  // non-residents and empleados keep seeing Personal. Default true while
  // hydrating (no flash).
  // 2026-09-24 (kimi review): 'inquilino' added to match the BE
  // hasResidentMembership role set (src/database/account-contexts.js), which
  // counts inquilino as resident-tier (ROLE_RANK 55 in routes.js); a
  // tenant bound to a unit IS a resident and must not lose the Personal lens.
  const RESIDENT_ROLES = ['residente', 'resident', 'familiar', 'inquilino'];
  const derivedIsResidentAnywhere = roles.some(
    (m) => !!m.unitId && !!m.role && RESIDENT_ROLES.includes(String(m.role).toLowerCase()),
  );
  const showPersonalFlag =
    residentAnywhereFlag !== null
      ? !residentAnywhereFlag
      : unitsLoaded
        ? !derivedIsResidentAnywhere
        : true;

  const refreshMe = async () => {
    const [response, unitsResponse] = await Promise.all([authAPI.getMe(), authAPI.getOwnedUnits()]);
    const unitsPayload = unitsResponse.data?.data || unitsResponse.data;
    const payload = response.data?.data || response.data;
    const nextUser = payload?.user || payload;
    // 2026-09-24: defensive — when /auth/me starts carrying the residency
    // flags (BE mirrors the /auth/contexts contract here), prefer server truth.
    captureResidencyFlags(payload);
    captureResidencyFlags(nextUser);
    let units: OwnedUnit[] = Array.isArray(unitsPayload) ? unitsPayload : unitsPayload?.units || [];
    // (Jaime 2026-08-31, unit-selector-as-context-driver) Merge employee-assigned
    // units from the /api/me context payload into the switcher unit list. The BE
    // child may expose them under any of the candidate keys below until its
    // payload ships; parse defensively and de-dupe by id. Employee-assigned
    // units behave exactly like owned units: selecting one sets the active unit
    // and the backend resolves the condo/role from that unit.
    const assignedPayloads: unknown[] = [
      payload?.assignedUnits, payload?.employeeUnits, payload?.employee_units, payload?.assigned_units,
      nextUser?.assignedUnits, nextUser?.employeeUnits, nextUser?.employee_units,
    ];
    for (const raw of assignedPayloads) {
      if (!Array.isArray(raw)) continue;
      for (const entry of raw) {
        const id = entry?.id || entry?.unitId || entry?.unit_id;
        if (!id) continue;
        const normalized: OwnedUnit = {
          id: String(id),
          unit_number: entry?.unit_number || entry?.unitNumber || entry?.household_name || entry?.name || '',
          household_name: entry?.household_name || entry?.householdName || entry?.name || null,
          condo_id: entry?.condo_id || entry?.condoId || null,
          condo_name: entry?.condo_name || entry?.condoName || null,
          assigned: true,
        };
        if (!units.some((u) => u.id === normalized.id)) units = [...units, normalized];
      }
    }
    setOwnedUnits(units);
    setUnitsLoaded(true);
    if (!nextUser) return;
    // i18n Stage 3: honor a server-side language change (e.g. from another device)
    // when this device has no explicit 'v-lang' choice.
    applyServerPreferredLanguage(nextUser);
    const nextRoles: CondoMembership[] = payload?.memberships || payload?.roles || nextUser.memberships || nextUser.roles || roles;
    const nextContexts: AccountContext[] = Array.isArray(payload?.contexts) ? payload.contexts : (Array.isArray(nextUser.contexts) ? nextUser.contexts : contexts);
    const serverActiveContextId = localStorage.getItem('vecindus_active_context') || payload?.activeContextId || nextUser.activeContextId;
    const serverActive = payload?.activeCondoId || nextUser.activeCondoId;
    // A locally selected context is authoritative; /auth/me can return the previously persisted server context.
    // Prefer the local selection so refreshMe does not snap the switcher back after the user changes condos.
    // If the user explicitly selected an unlinked home unit (personal context) and no condo
    // is stored, do not resurrect the first membership condo — that would drop the home unit below.
    const storedUnitIdEarly = localStorage.getItem('vecindus_active_owned_unit');
    const storedUnitEarly = storedUnitIdEarly ? units.find((u) => u.id === storedUnitIdEarly) : undefined;
    const personalByUnit = !localStorage.getItem('vecindus_active_condo') && !!storedUnitEarly && !storedUnitEarly.condo_id;
    // TH1 personal-lens lane (2026-10-01, super-admin handoff): when entering
    // the app with no persisted condo selection (e.g. handoff from the
    // super-admin panel wiped the active-context state), prefer the user's
    // REAL last condo from the per-condo memory path (vecindus_last_condo)
    // over the roles[0] default — the stale-repair chain below then restores
    // the last-selected unit, so the header and the unit-page data agree
    // instead of showing a borrowed/empty context.
    let selected = localStorage.getItem('vecindus_active_condo') || (personalByUnit ? undefined : (activeCondoId || serverActive || localStorage.getItem('vecindus_last_condo') || nextRoles[0]?.condoId));
    // Drop stale selections (e.g. deleted/recreated condos): only keep ids the user actually has access to
    if (selected && nextRoles.length > 0) {
      const ownedCondoIds = units.map((u) => u.condo_id).filter(Boolean);
      if (!nextRoles.some((entry) => entry.condoId === selected) && !ownedCondoIds.includes(selected)) {
        selected = nextRoles[0]?.condoId;
        if (selected) localStorage.setItem('vecindus_active_condo', selected); else localStorage.removeItem('vecindus_active_condo');
      }
    }
    const activeContext = nextContexts.find((entry) => entry.id === serverActiveContextId);
    const isPersonalContext = activeContext?.kind === 'personal';
    if (isPersonalContext || personalByUnit) {
      selected = undefined;
      localStorage.removeItem('vecindus_active_condo');
    }
    // Restore the previously active owned unit so a condo context switch keeps unit info after reload.
    // #178 (2026-09-25): a stored unit may also be a MEMBERSHIP unit (resident
    // with 2+ units in one condo — it is not in /units/mine). Keep it active
    // when a membership row backs it and matches the stored condo, instead of
    // silently dropping the selection on every reload.
    let restoredUnit: OwnedUnit | undefined;
    let restoredUnitId: string | undefined;
    const storedUnitId = localStorage.getItem('vecindus_active_owned_unit');
    if (storedUnitId) {
      const candidate = units.find((u) => u.id === storedUnitId);
      if (candidate) {
        const belongsToSelected = selected ? candidate.condo_id === selected : !candidate.condo_id;
        if (belongsToSelected) {
          restoredUnit = candidate;
          restoredUnitId = candidate.id;
          // Seed the per-condo last-selected memory (Jaime 2026-09-30).
          setLastUnitForCondo(candidate.condo_id ?? null, storedUnitId);
        } else {
          localStorage.removeItem('vecindus_active_owned_unit');
        }
      } else {
        const storedCondoId = localStorage.getItem('vecindus_active_condo');
        const membershipBacks = (nextRoles || []).some((entry) => ((entry.unitId || (entry as unknown as Record<string, unknown>).unit_id) as string | undefined) === storedUnitId && (!storedCondoId || !entry.condoId || entry.condoId === storedCondoId));
        if (membershipBacks) {
          restoredUnitId = storedUnitId;
          setLastUnitForCondo(storedCondoId ?? null, storedUnitId);
        } else {
          localStorage.removeItem('vecindus_active_owned_unit');
        }
      }
    }
    // Jaime 2026-09-30: when the globally stored unit was dropped (stale or
    // belongs to another condo), repair from the per-condo last-selected
    // memory. resolveLastUnitForCondo never destroys memory on a miss (Kimi
    // TH1 stale-id lane (2026-10-01): api-level stale-unit guard snapshot —
    // combined owned + membership unit ids, refreshed on every refreshMe.
    try {
      const knownUnitIds = new Set<string>(units.map((u) => u.id));
      for (const entry of nextRoles || []) {
        const mid = (entry.unitId || (entry as unknown as Record<string, unknown>).unit_id) as string | undefined;
        if (mid) knownUnitIds.add(mid);
      }
      localStorage.setItem('vecindus_known_unit_ids', JSON.stringify(Array.from(knownUnitIds)));
    } catch { /* storage optional */ }
    // TH1 stale-id lane (2026-10-01): a memory repair is additionally VERIFIED
    // against the freshly loaded units/memberships — the closure state can be
    // stale on boot and a deleted unit id must never be restored. When the
    // per-condo memory itself is dead, the key is dropped immediately and
    // (condo lens only) the default unit of the selected condo is restored
    // with repaired:true — no api call may fire while the active unit id is
    // not a real unit. Personal lens: never inject a condo unit (dip contract).
    let repaired = false;
    if (!restoredUnitId) {
      // #306: capture the per-condo memory id BEFORE any invalidation so the
      // beacon distinguishes a real stale repair (remembered id existed but
      // failed verification) from normal first-boot default adoption (no
      // memory at all — no repair happened, nothing to beacon).
      const rememberedId = resolveLastUnitForCondo(selected ?? undefined)?.id;
      let repair = resolveLastUnitForCondo(selected ?? undefined);
      if (repair?.id) {
        const inUnits = units.some((u) => u.id === repair!.id && (selected ? u.condo_id === selected : !u.condo_id));
        const membershipBacks = (nextRoles || []).some((entry) => {
          const mid = (entry.unitId || (entry as unknown as Record<string, unknown>).unit_id) as string | undefined;
          if (mid !== repair!.id) return false;
          const mcondo = (entry.condoId || (entry as unknown as Record<string, unknown>).condo_id) as string | null | undefined;
          return selected ? (!mcondo || mcondo === selected) : !mcondo;
        });
        if (!inUnits && !membershipBacks) repair = undefined;
      }
      if (!repair?.id) {
        // Dead memory: drop the per-condo key NOW so no later restore can
        // resurrect the dead id.
        setLastUnitForCondo(selected ?? null, undefined);
        if (selected && units.length > 0) {
          const fallback = units.find((u) => u.condo_id === selected) || units[0];
          if (fallback) {
            if (rememberedId) {
              // #306: real stale repair — a remembered per-condo id existed but
              // failed verification. Fresh-session default adoption stays silent.
              traceSel('staleUnitRepaired', { condoId: selected, deadUnitId: rememberedId ?? null, fallbackUnitId: fallback.id });
              // Beacon: leading indicator of the TH1 stale-id class (session-deduped).
              beaconStateAnomaly({ event: 'staleUnitRepaired', deadUnitId: rememberedId ?? null, condoId: selected ?? null, fallbackUnitId: fallback.id, repaired: true });
            }
            repair = fallback;
          }
        }
      }
      if (repair?.id) {
        restoredUnitId = repair.id;
        if (repair.unit_number || repair.household_name) restoredUnit = repair;
        localStorage.setItem('vecindus_active_owned_unit', repair.id);
        setLastUnitForCondo(selected ?? null, repair.id);
        repaired = true;
      }
    }
    setActiveOwnedUnitId(restoredUnitId);
    traceSel('refreshMe', { selectedCondo: selected ?? null, storedUnitId, restoredUnitId: restoredUnitId ?? null, repaired, ownedUnitsLoaded: units.length });
    const membership = nextRoles.find((entry) => !!restoredUnitId && ((entry.unitId || (entry as unknown as Record<string, unknown>).unit_id) as string | undefined) === restoredUnitId)
      || nextRoles.find((entry) => entry.condoId === selected);
    const resolvedUnitLabel = membership?.unitLabel || membership?.unitNumber || restoredUnit?.household_name || restoredUnit?.unit_number || nextUser.unitLabel || nextUser.unit; // FIX 2026-08-30: prefer unit name over number
    // P0-2 fix: trust the server role; only fall back to the client role if the server omitted it.
    const resolvedRole = nextUser.role || user?.role;
    const merged = { ...user, ...nextUser, role: resolvedRole, roles: nextRoles, memberships: nextRoles, contexts: nextContexts, activeContextId: serverActiveContextId, condoId: selected || (isPersonalContext ? '' : nextUser.condoId), unitLabel: resolvedUnitLabel, unit: resolvedUnitLabel } as User;
    setContexts(nextContexts); setActiveContextId(serverActiveContextId); setRoles(nextRoles); setActiveCondoId(selected); setUser(merged);
  };

  // Hydrate roles/user from /auth/me for sessions restored from storage
  // (sessions created before roles[] existed would otherwise stay empty)
  useEffect(() => {
    if (isAuthenticated) {
      refreshMe().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const logout = async () => {
    setUnitsLoaded(false);
    setIsAuthenticated(false);
    setUser(null);
    // Capture the refresh token BEFORE clearAuth() wipes localStorage, so the
    // server can revoke the full pair (2026-09-29 token revocation).
    const storedRefresh = localStorage.getItem('refreshToken');
    const storedAccess = localStorage.getItem('token');
    clearAuth();
  // Await the revocation POST before redirecting: a full page navigation
  // fired in the same tick aborts the in-flight request and the server never
  // revokes the pair. authAPI.logout swallows errors internally (warns, never
  // throws) and clears localStorage on both paths, so awaiting is safe.
    await authAPI.logout(storedRefresh, storedAccess).catch(() => {});
  // Redirect to login page after revocation completes (or fails harmlessly)
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      loginWithPassword,
      completeLoginWithPayload,
      verify2FA, 
      logout, 
      loading,
      hasRole,
      isSuperAdmin,
      isAdmin,
      isDirective,
      isResident,
      isSecurity,
      contexts, activeContextId, activateContext, activatePersonalContext, roles, memberships: roles, activeCondoId, setActiveCondo, ownedUnits, activeOwnedUnitId, setActiveOwnedUnit, refreshMe, unitsLoaded, hasUnits, showPersonalFlag
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
